import { useState, useEffect, useRef } from "react";
import { X, Send, Check, ArrowLeft, Loader2, Cross } from "lucide-react";

/**
 * Generic conversational interview component.
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - title: header title
 *  - questions: array of question objects
 *  - onSubmit: async (answers) => void
 *  - introMessage: string greeting
 */
export default function ChatInterview({ open, onClose, title, questions, onSubmit, introMessage, submitLabel = "Valider" }) {
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [history, setHistory] = useState([]); // {role:'bot'|'user', text}
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open) {
      setIdx(0); setAnswers({}); setDraft(""); setDone(false);
      setHistory([
        { role: "bot", text: introMessage || "Bonjour ! Je vais vous poser quelques questions pour avancer ensemble." },
        { role: "bot", text: questions[0].question },
      ]);
    }
  }, [open]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [history, idx]);

  if (!open) return null;

  const current = questions[idx];

  const formatAnswer = (q, val) => {
    if (q.type === "multi") return val?.length ? val.join(", ") : "—";
    if (q.type === "boolean") return val ? "Oui" : "Non";
    return String(val ?? "—");
  };

  const advance = (value) => {
    const updated = { ...answers, [current.key]: value };
    setAnswers(updated);
    const userText = formatAnswer(current, value);
    const nextIdx = idx + 1;
    const newHistory = [...history, { role: "user", text: userText }];
    if (nextIdx < questions.length) {
      newHistory.push({ role: "bot", text: questions[nextIdx].question });
      setHistory(newHistory);
      setIdx(nextIdx);
      setDraft("");
    } else {
      newHistory.push({ role: "bot", text: "✓ J'ai tout ce qu'il me faut. Cliquez sur « " + submitLabel + " » pour confirmer." });
      setHistory(newHistory);
      setDone(true);
    }
  };

  const submitNow = async () => {
    setBusy(true);
    try {
      await onSubmit(answers);
      onClose();
    } finally {
      setBusy(false);
    }
  };

  const handleTextSubmit = (e) => {
    e?.preventDefault();
    if (!draft.trim() && current.required !== false) return;
    advance(current.type === "number" ? Number(draft) : draft);
  };

  const progress = Math.round(((idx + (done ? 1 : 0)) / questions.length) * 100);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm grid place-items-center p-2 sm:p-4" data-testid="chat-interview">
      <div className="bg-white rounded-3xl w-full max-w-2xl h-[85vh] flex flex-col overflow-hidden border border-[#E2E5E0] shadow-2xl">
        <header className="flex items-center justify-between p-4 sm:p-5 border-b border-[#E2E5E0]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#1A4D2E] grid place-items-center text-white"><Cross className="w-4 h-4" /></div>
            <div>
              <h3 className="font-medium text-base" style={{ fontFamily: "Outfit" }}>{title}</h3>
              <p className="text-xs text-[#6B726A]">Assistant Apocia</p>
            </div>
          </div>
          <button onClick={onClose} data-testid="close-interview" className="w-9 h-9 rounded-full hover:bg-[#F0F2EF] grid place-items-center"><X className="w-4 h-4" /></button>
        </header>

        <div className="h-1 bg-[#F0F2EF]">
          <div className="h-full bg-[#1A4D2E] transition-all" style={{ width: `${progress}%` }} />
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-3 bg-[#F7F8F6]">
          {history.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div
                className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                  m.role === "user"
                    ? "bg-[#1A4D2E] text-white rounded-br-sm"
                    : "bg-white border border-[#E2E5E0] text-[#1f2421] rounded-bl-sm"
                }`}
              >
                {m.text}
              </div>
            </div>
          ))}
        </div>

        <footer className="border-t border-[#E2E5E0] p-4 bg-white">
          {done ? (
            <div className="flex items-center justify-between gap-3">
              <button onClick={() => { setIdx(idx - 1); setDone(false); setHistory(history.slice(0, -2)); }} className="text-sm text-[#6B726A] flex items-center gap-1">
                <ArrowLeft className="w-4 h-4" /> Modifier la dernière réponse
              </button>
              <button onClick={submitNow} disabled={busy} data-testid="submit-interview" className="rp-btn-primary flex items-center gap-2 disabled:opacity-60">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                {submitLabel}
              </button>
            </div>
          ) : current.type === "choice" ? (
            <div className="flex flex-wrap gap-2">
              {current.options.map((opt) => (
                <button key={opt.value ?? opt} type="button" onClick={() => advance(opt.value ?? opt)}
                  data-testid={`choice-${current.key}-${opt.value ?? opt}`}
                  className="px-4 py-2 rounded-full border border-[#E2E5E0] hover:border-[#1A4D2E] hover:bg-[#E8F0EA] text-sm transition-all">
                  {opt.label ?? opt}
                </button>
              ))}
            </div>
          ) : current.type === "multi" ? (
            <MultiChoice question={current} onSubmit={advance} />
          ) : current.type === "boolean" ? (
            <div className="flex gap-2">
              <button onClick={() => advance(true)} className="rp-btn-primary flex-1" data-testid={`bool-${current.key}-yes`}>Oui</button>
              <button onClick={() => advance(false)} className="rp-btn-secondary flex-1" data-testid={`bool-${current.key}-no`}>Non</button>
            </div>
          ) : (
            <form onSubmit={handleTextSubmit} className="flex items-center gap-2">
              <input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                type={current.type === "number" ? "number" : current.type === "date" ? "date" : current.type === "time" ? "time" : "text"}
                placeholder={current.placeholder || "Votre réponse…"}
                autoFocus
                data-testid={`input-${current.key}`}
                className="flex-1 bg-[#F7F8F6] border border-[#E2E5E0] rounded-full px-4 py-2.5 focus:bg-white focus:border-[#1A4D2E] focus:ring-2 focus:ring-[#1A4D2E]/15 outline-none text-sm"
              />
              <button type="submit" data-testid="send-answer" className="w-11 h-11 rounded-full bg-[#1A4D2E] text-white grid place-items-center hover:bg-[#143C24]">
                <Send className="w-4 h-4" />
              </button>
            </form>
          )}
          {!done && current.skippable && (
            <button onClick={() => advance(current.type === "multi" ? [] : "")} className="text-xs text-[#6B726A] mt-2 hover:text-[#1f2421]">
              Passer cette question
            </button>
          )}
        </footer>
      </div>
    </div>
  );
}

function MultiChoice({ question, onSubmit }) {
  const [sel, setSel] = useState([]);
  const toggle = (v) => setSel((s) => (s.includes(v) ? s.filter((x) => x !== v) : [...s, v]));
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-3">
        {question.options.map((o) => {
          const val = o.value ?? o;
          const active = sel.includes(val);
          return (
            <button key={val} type="button" onClick={() => toggle(val)}
              data-testid={`multi-${question.key}-${val}`}
              className={`px-3 py-1.5 rounded-full text-xs border transition-all ${
                active ? "bg-[#1A4D2E] text-white border-[#1A4D2E]" : "border-[#E2E5E0] text-[#1f2421] hover:border-[#1A4D2E]"
              }`}>
              {o.label ?? o}{active && " ✓"}
            </button>
          );
        })}
      </div>
      <button onClick={() => onSubmit(sel)} data-testid={`multi-confirm-${question.key}`} className="rp-btn-primary inline-flex items-center gap-2">
        <Check className="w-4 h-4" /> Valider {sel.length > 0 && `(${sel.length})`}
      </button>
    </div>
  );
}
